import { createContext, useState, useMemo, useContext, useEffect, useCallback } from "react";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";

/*
Konsumierende Komponenten, die den Kontext verwenden, könnten unnötig neu gerendert werden, wenn sich der Kontextwert ändert.
Regel für Konsumierende Komponenten:
-Verwenden von React.memo, um diese Komponenten zu memoisieren und unnötige Re-Renders zu verhindern.
Implementierung:
-const MyComponent = React.memo(() => {
    const colors = useColors();
    // Komponentencode
 });
*/


// Funktion zur Erkennung des bevorzugten Farbschemas auf Basis der Windows-Einstellungen für Apps
const _getPreferredColorScheme = () => {
    return window?.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

//color design tokens
const _getTokens = (() => {
    const cache = {};
    return (mode) => {
        if (cache[mode]) {
            return cache[mode];
        }
        const tokens = {
            ...(mode === "dark"
                ? {
                    lightBlack: {
                        100: "#d7d8d8",
                        200: "#b0b1b1",
                        300: "#888989",
                        400: "#616262",
                        500: "#393b3b",
                        600: "#2e2f2f",
                        700: "#222323",
                        800: "#171818",
                        900: "#0b0c0c"
                    },
                    darkBlack: {
                        100: "#d1d1d1",
                        200: "#a4a4a4",
                        300: "#767676",
                        400: "#494949",
                        500: "#1b1b1b",
                        600: "#161616",
                        700: "#101010",
                        800: "#0b0b0b",
                        900: "#050505"
                    },
                    white: {
                        100: "#ffffff",
                        200: "#fefefe",
                        300: "#fdfdfd",
                        400: "#fcfcfc",
                        500: "#fbfbfb",
                        600: "#cacaca",
                        700: "#979797",
                        800: "#656565",
                        900: "#323232"
                    },
                    grey: {
                        100: "#e0e0e0",
                        200: "#c2c2c2",
                        300: "#a3a3a3",
                        400: "#858585",
                        500: "#666666",
                        600: "#525252",
                        700: "#3d3d3d",
                        800: "#292929",
                        900: "#141414"
                    },
                    lightBlue: {
                        100: "#ccf9fb",
                        200: "#99f4f7",
                        300: "#66eef4",
                        400: "#33e9f0",
                        500: "#00e3ec",
                        600: "#00b6bd",
                        700: "#00888e",
                        800: "#005b5e",
                        900: "#002d2f"
                    },
                    darkBlue: {
                        100: "#cce2e9",
                        200: "#99c4d3",
                        300: "#66a7bc",
                        400: "#3389a6",
                        500: "#006c90",
                        600: "#005673",
                        700: "#004156",
                        800: "#002b3a",
                        900: "#00161d"
                    },
                    blueGrey: {
                        100: "#f7fafb",
                        200: "#f0f4f7",
                        300: "#e8eff3",
                        400: "#e1e9ef",
                        500: "#d9e4eb",
                        600: "#aeb6bc",
                        700: "#82898d",
                        800: "#575b5e",
                        900: "#2b2e2f"
                    },
                    greenAccent: {
                        100: "#dbf5ee",
                        200: "#b7ebde",
                        300: "#94e2cd",
                        400: "#70d8bd",
                        500: "#4cceac",
                        600: "#3da58a",
                        700: "#2e7c67",
                        800: "#1e5245",
                        900: "#0f2922"
                    },
                    redAccent: {
                        100: "#f8dcdb",
                        200: "#f1b9b7",
                        300: "#e99592",
                        400: "#e2726e",
                        500: "#db4f4a",
                        600: "#af3f3b",
                        700: "#832f2c",
                        800: "#58201e",
                        900: "#2c100f"
                    },
                    blueAccent: {
                        100: "#e1e2fe",
                        200: "#c3c6fd",
                        300: "#a4a9fc",
                        400: "#868dfb",
                        500: "#6870fa",
                        600: "#535ac8",
                        700: "#3e4396",
                        800: "#2a2d64",
                        900: "#151632"
                    }
                }
                : {
                    lightBlack: {
                        100: "#0b0c0c",
                        200: "#171818",
                        300: "#222323",
                        400: "#2e2f2f",
                        500: "#393b3b",
                        600: "#616262",
                        700: "#888989",
                        800: "#b0b1b1",
                        900: "#d7d8d8"
                    },
                    darkBlack: {
                        100: "#050505",
                        200: "#0b0b0b",
                        300: "#101010",
                        400: "#161616",
                        500: "#1b1b1b",
                        600: "#494949",
                        700: "#767676",
                        800: "#a4a4a4",
                        900: "#d1d1d1"
                    },
                    white: {
                        100: "#323232",
                        200: "#656565",
                        300: "#979797",
                        400: "#cacaca",
                        500: "#fbfbfb",
                        600: "#fcfcfc",
                        700: "#fdfdfd",
                        800: "#fefefe",
                        900: "#ffffff"
                    },
                    grey: {
                        100: "#141414",
                        200: "#292929",
                        300: "#3d3d3d",
                        400: "#525252",
                        500: "#666666",
                        600: "#858585",
                        700: "#a3a3a3",
                        800: "#c2c2c2",
                        900: "#e0e0e0"
                    },
                    lightBlue: {
                        100: "#002d2f",
                        200: "#005b5e",
                        300: "#00888e",
                        400: "#00b6bd",
                        500: "#00e3ec",
                        600: "#33e9f0",
                        700: "#66eef4",
                        800: "#99f4f7",
                        900: "#ccf9fb"
                    },
                    darkBlue: {
                        100: "#00161d",
                        200: "#002b3a",
                        300: "#004156",
                        400: "#005673",
                        500: "#006c90",
                        600: "#3389a6",
                        700: "#66a7bc",
                        800: "#99c4d3",
                        900: "#cce2e9"
                    },
                    blueGrey: {
                        100: "#2b2e2f",
                        200: "#575b5e",
                        300: "#82898d",
                        400: "#aeb6bc",
                        500: "#d9e4eb",
                        600: "#e1e9ef",
                        700: "#e8eff3",
                        800: "#f0f4f7",
                        900: "#f7fafb"
                    },
                    greenAccent: {
                        100: "#0f2922",
                        200: "#1e5245",
                        300: "#2e7c67",
                        400: "#3da58a",
                        500: "#4cceac",
                        600: "#70d8bd",
                        700: "#94e2cd",
                        800: "#b7ebde",
                        900: "#dbf5ee"
                    },
                    redAccent: {
                        100: "#2c100f",
                        200: "#58201e",
                        300: "#832f2c",
                        400: "#af3f3b",
                        500: "#db4f4a",
                        600: "#e2726e",
                        700: "#e99592",
                        800: "#f1b9b7",
                        900: "#f8dcdb"
                    },
                    blueAccent: {
                        100: "#151632",
                        200: "#2a2d64",
                        300: "#3e4396",
                        400: "#535ac8",
                        500: "#6870fa",
                        600: "#868dfb",
                        700: "#a4a9fc",
                        800: "#c3c6fd",
                        900: "#e1e2fe"
                    }
                })
        };
        cache[mode] = tokens;
        return tokens;
    };
})();

// debounce.js
const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
};

// Theme Cache
const themeCache = {};

const _getTheme = (mode) => {
    if (themeCache[mode]) {
        return themeCache[mode];
    }
    const colors = _getTokens(mode);
    const theme = createTheme({
        palette: {
            mode: mode,
            ...(mode === 'dark'
                ? {
                    primary: {
                        main: colors.lightBlack[500]
                    },
                    secondary: {
                        main: colors.darkBlue[500]
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100]
                    },
                    background: {
                        default: colors.lightBlack[500]
                    }
                }
                : {
                    primary: {
                        main: colors.lightBlack[100]
                    },
                    secondary: {
                        main: colors.darkBlue[500]
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100]
                    },
                    background: {
                        default: colors.lightBlack[900]
                    }
                }
            ),
        },
        typography: {
            fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
            fontSize: 12,
            h1: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 40,
            },
            h2: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 32,
            },
            h3: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 24,
            },
            h4: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 20,
            },
            h5: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 16,
            },
            h6: {
                fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
                fontSize: 14,
                marginBottom: "16px",
                marginTop: "16px",
            }
        }
    });
    themeCache[mode] = theme;
    return theme;
};

const useMode = () => {
    const [mode, setMode] = useState(() => _getPreferredColorScheme());

    const toggleColorMode = useCallback(() => {
        setMode((prev) => (prev === "light" ? "dark" : "light"));
    }, [setMode]);

    const handleChange = useCallback(
        debounce((e) => {
            setMode(e.matches ? "dark" : "light");
        }, 100),
        [setMode]
    );

    // Effect to listen for changes in the system's color scheme
    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handleChange);
        } else {
            mediaQuery.addListener(handleChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", handleChange);
            } else {
                mediaQuery.removeListener(handleChange);
            }
        };
    }, [handleChange]);

    const colors = useMemo(() => _getTokens(mode), [mode]);

    const theme = useMemo(() => _getTheme(mode), [mode]);

    return { theme, toggleColorMode, colors };
}

// context for color mode
const ColorModeContext = createContext();
const ColorsContext = createContext();

export const ColorModeProvider = ({ children }) => {
    const { theme, toggleColorMode, colors } = useMode();

    /*
        ToDo:
        Die kompletten light und dark-Theme einstellungen Überarbeiten und auf die Elemente der WebSeite anwenden!
        Macht es Sinn die toggleColorMode-Funktion noch zustätzlich zu memorisieren wie in dem folgenden Beispiel?
        Denn die toggleColorMode-Funktion baut bereits auf useCallback auf! 
        const colorModeContextValue = useMemo(() => ({ toggleColorMode }), [toggleColorMode]);
    */
    return (
        <ColorModeContext.Provider value={{ toggleColorMode }}>
            <ColorsContext.Provider value={colors}>
                <ThemeProvider theme={theme}>                   {/* Der ThemeProvider von Material-UI wendet das berechnete theme auf die gesamte Anwendung an. Das sorgt dafür, dass die in themeSettings definierten Farben, Typografie und andere Theme-Einstellungen in der gesamten Anwendung konsistent angewendet werden. */}
                    <CssBaseline />                             {/* CssBaseline sorgt dafür, dass das Standard-Styling von Material-UI angewendet wird */}
                    {children}
                </ThemeProvider>
            </ColorsContext.Provider>
        </ColorModeContext.Provider>
    );
};

export const useColorMode = () => useContext(ColorModeContext);
export const useColors = () => useContext(ColorsContext);